<template>
  <div class="holder-profile-form">
    <div class="holder-2fa">
      <div class="twofa-text">
        <p>How would you like to receive your authentication code?</p>
      </div>
      <div class="toggleWrapper">
        <input
          id="email-2fa"
          v-model="checked2fa"
          type="radio"
          name="2fa"
          value="EMAIL_2FA"
          :disabled="default2fa === 'G2FA'"
        >
        <label for="email-2fa" class="toggle">
          <div class="radio-label-wrapper">
            <span class="toggle__handler">Via Email Secret Code</span>
            <span class="toggle__handler">We will send a secret code to your email</span>
          </div>
        </label>
      </div>
      <div class="toggleWrapper">
        <input
          id="sms-2fa"
          v-model="checked2fa"
          type="radio"
          name="2fa"
          value="SMS_2FA"
          :disabled="default2fa === 'G2FA'"
        >
        <label for="sms-2fa" class="toggle">
          <div class="radio-label-wrapper">
            <span class="toggle__handler">Via SMS Text Message</span>
            <span class="toggle__handler">We will send a SMS code to your phone number</span>
          </div>
        </label>
      </div>
      <div class="toggleWrapper">
        <input
          id="g2fa"
          v-model="checked2fa"
          type="radio"
          name="2fa"
          value="G2FA"
        >
        <label for="g2fa" class="toggle">
          <div class="radio-label-wrapper">
            <span class="toggle__handler">Via the Google Authenticator</span>
            <span class="toggle__handler">Use an app to generate an authentication code</span>
          </div>
        </label>
      </div>
      <div
        v-if="checked2fa !== 'G2FA'"
        id="save_2fa_state"
        ref="save2FaState"
        style="margin: 50px 0 0 0"
      >
        <button
          id="save_2fa_state_button"
          :disabled="checked2fa === default2fa"
          class="btn profile-btn save-btn"
          @click="check2fa"
        >
          Save
        </button>
        <tooltip v-if="default2fa === 'G2FA' && checked2fa !== 'G2FA'" :target-ref="$refs.save2FaState">
          <span>To change verification type you need to disable google authenticator</span>
        </tooltip>
      </div>
      <div v-if="checked2fa === 'G2FA' && default2fa !== 'G2FA'" class="verification-2fa-wrap">
        <div class="verification-2fa-step">
          <h2 class="verification-2fa-title">
            1. Configure the app
          </h2>
          <p class="verification-2fa-descr">
            Open Google Authenticator app and scan the QR-code:
          </p>
          <div class="verification-2fa-qr-wrap">
            <img
              v-if="qrCode"
              :src="`data:image/png;base64,${qrCode}`"
              class="verification-2fa-qr-image"
              alt="qr code"
            >
          </div>
          <div class="verification-2fa-secret-wrap">
            <span class="verification-2fa-secret-title">Secret key</span>
            <span class="verification-2fa-secret-value">{{ securityCode }}</span>
          </div>
        </div>
        <div class="verification-2fa-step">
          <h2 class="verification-2fa-title">
            2. Enter the 6-digit code
          </h2>
          <div class="verification-2fa-otp-wrap">
            <otp-code
              ref="otpInput"
              :otp-verify="otpVerify"
              @update:otpValue="setGoogleTwoFa"
            />
          </div>
        </div>
      </div>
      <div v-if="checked2fa === 'G2FA' && default2fa === 'G2FA'" class="verification-2fa-wrap">
        <div class="verification-2fa-step">
          <h2 class="verification-2fa-title" style="margin-bottom: 10px;">
            Disable Google Authenticator
          </h2>
          <p class="verification-2fa-descr">
            In order to change the type of 2FA, you will first need to disable the
            Google Authenticator.
          </p>
        </div>
        <div class="verification-2fa-step">
          <h2 class="verification-2fa-title">
            Enter the 6-digit code
          </h2>
          <div class="verification-2fa-otp-wrap">
            <otp-code
              ref="otpInput"
              :otp-verify="otpVerify"
              @update:otpValue="disableGTwoFaOtp"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="sessions-wrapper">
      <div class="sessions-header">
        <p>Sessions</p>
      </div>
      <table cellpadding="0" cellspacing="0" border="0">
        <TableHeader>
          <tr>
            <th>
              <div
                :id="`sort_sessions_by_browser`"
                class="table-header-box"
                :class="{
                  'sort-active': sortBy === 'browser',
                  'sorted-desc': order === DESC,
                }"
                @click="sortedBy('browser')"
              >
                <DevicesIcon16 />
                <span class="header-table-text">Browser</span>
                <arrowDown class="arrow" />
              </div>
            </th>
            <th>
              <div
                :id="`sort_sessions_by_os`"
                class="table-header-box"
                :class="{
                  'sort-active': sortBy === 'os',
                  'sorted-desc': order === DESC,
                }"
                @click="sortedBy('os')"
              >
                <WhiteDocIcon18 />
                <span class="header-table-text">OS</span>
                <arrowDown class="arrow" />
              </div>
            </th>
            <th>
              <div
                :id="`sort_sessions_by_location`"
                class="table-header-box"
                :class="{
                  'sort-active': sortBy === 'location',
                  'sorted-desc': order === DESC,
                }"
                @click="sortedBy('location')"
              >
                <WhiteSquareArrowIcon20 />
                <span class="header-table-text">Last Login Location</span>
                <arrowDown class="arrow" />
              </div>
            </th>
            <th>
              <div
                :id="`sort_sessions_by_time`"
                class="table-header-box"
                :class="{
                  'sort-active': sortBy === 'lastLoginTime',
                  'sorted-desc': order === DESC,
                }"
                @click="sortedBy('lastLoginTime')"
              >
                <DateIcon16 />
                <span class="header-table-text">Last Login Time</span>
                <arrowDown class="arrow" />
              </div>
            </th>
          </tr>
        </TableHeader>
        <Loader v-if="loadFlag" :load-flag="loadFlag" />
        <template v-else>
          <UserSessionItem :user-sessions-list="userSessionsList" />
        </template>
      </table>
    </div>

    <CommonDialog ref="commonDialogTwoFaSuccess">
      <SuccessIcon />
      <h2 class="dialog-message-title">
        Success!
      </h2>
      <p v-if="checked2fa === 'G2FA' || isG2Disabled" class="dialog-message-description" style="display:block;">
        Google
        authenticator was successfully
        {{ isG2Disabled ? 'disabled and 2fa was switched to email verification' : 'enabled' }}
      </p>
      <p v-else class="dialog-message-description" style="display:block;">
        The 2FA was successfully changed
      </p>
    </CommonDialog>
  </div>
</template>

<script>
import CommonDialog from '../popups/CommonDialog';
import OtpCode from './OtpCode';
import Tooltip from '@/components/common/Tooltip.vue';
import Loader from '@/components/loader/Loader.vue';
import ArrowDown from '@/components/icons/ArrowDown.vue';
import TableHeader from '@/components/table/TableHeader.vue';
import {ASC, DESC} from '@/constants/const';
import UserSessionItem from '@/components/profile/UserSessionItem.vue';
import errorHandler from '@/mixins/errorHandler';
import DevicesIcon16 from '@/components/icons/DevicesIcon16.vue';
import WhiteDocIcon18 from '@/components/icons/WhiteDocIcon18.vue';
import WhiteSquareArrowIcon20 from '@/components/icons/WhiteSquareArrowIcon20.vue';
import DateIcon16 from '@/components/icons/DateIcon16.vue';
import SuccessIcon from '@/components/icons/SuccessIcon.vue';

export default {
  components: {
    SuccessIcon,
    DateIcon16,
    WhiteSquareArrowIcon20,
    WhiteDocIcon18,
    DevicesIcon16,
    UserSessionItem,
    TableHeader,
    ArrowDown,
    Loader,
    Tooltip,
    CommonDialog,
    OtpCode,
  },
  mixins: [errorHandler],
  data() {
    return {
      ASC,
      DESC,
      sortBy: 'browser',
      order: '',
      errorMessage: '',
      checked2fa: '',
      default2fa: null,
      isG2Disabled: false,
      qrCode: '',
      securityCode: '',
      otpVerify: null,
      loadFlag: false,
      userSessionsList: [],
      userPublicId: '',
    };
  },
  watch: {
    checked2fa(newVal) {
      if (newVal === 'G2FA') {
        this.profileGoogleTwoFa();
      }
    }
  },
  async mounted() {
    this.userPublicId = this.$store.state.profile.user.publicId;
    await this.checkDefaultTwoFaType();
    await this.getUserSessions();
  },
  methods: {
    openDialogTwoFaSuccess() {
      this.$refs.commonDialogTwoFaSuccess.openPopups();
    },
    checkProfileGoogleTwoFa() {
      this.$load(async () => {
        await this.$api.profile.checkProfileGoogleTwoFa().then((res) => {
            if (res.data.body.result === true) {
              this.checked2fa = 'G2FA';
              this.default2fa = 'G2FA';
            }
          }, (error => {
            this.handleResponseError(error);
          })
        );
      });
    },
    setGoogleTwoFa(value) {
      this.$load(async () => {
        await this.$api.profile.confirmProfileGoogleTwoFa({code: value}).then(() => {
          this.$load(async () => {
            this.$api.profile.changeVerificationType({type: 'G2FA'}).then(() => {
              this.otpVerify = true;
              this.default2fa = 'G2FA';
              this.isG2Disabled = false;
              this.$refs.otpInput.handleClearInput();
              this.openDialogTwoFaSuccess();
            });
          });
        });
      }, (error) => {
        this.otpVerify = false;
        this.errorMessage = error.data.errors.message[0];
      });
    },
    profileGoogleTwoFa() {
      this.$load(async () => {
        await this.$api.profile.profileGoogleTwoFa({}).then((res) => {
            let response = res.data.body;
            this.getGoogleTwoFaQr();
            this.securityCode = response.securityCode;
          }, (error => {
            this.handleResponseError(error);
          })
        );
      });
    },
    getGoogleTwoFaQr() {
      this.$load(async () => {
        await this.$api.profile.getGoogleTwoFaQr({}).then((res) => {
            this.qrCode = res.data.body.encodedQrCode;
          }, (error => {
            this.handleResponseError(error);
          })
        );
      });
    },
    disableGTwoFaOtp(value) {
      this.$load(async () => {
        await this.$api.profile.disableProfileGoogleTwoFa({code: value}).then(() => {
          this.$api.profile.changeVerificationType({type: 'EMAIL_2FA'}).then(() => {
            this.otpVerify = true;
            this.isG2Disabled = true;
            this.default2fa = 'EMAIL_2FA';
            this.checked2fa = 'EMAIL_2FA';
            this.openDialogTwoFaSuccess();
          });
        });
      }, (error) => {
        this.otpVerify = false;
        this.handleResponseError(error);
      });
    },
    check2fa() {
      this.resetState();
      if (this.default2fa !== this.checked2fa) {
        if (this.checked2fa === 'G2FA') {
          this.profileGoogleTwoFa();
        } else if (this.checked2fa === 'EMAIL_2FA') {
          this.$load(async () => {
            this.$api.profile.changeVerificationType({type: this.checked2fa}).then(() => {
              this.default2fa = this.checked2fa;
              this.openDialogTwoFaSuccess();
            });
          });
        } else if (this.checked2fa === 'SMS_2FA') {
          this.$load(async () => {
            this.$api.profile.changeVerificationType({type: this.checked2fa}).then(() => {
              this.default2fa = this.checked2fa;
              this.openDialogTwoFaSuccess();
            });
          });
        }
      }
    },
    async checkDefaultTwoFaType() {
      await this.checkProfileGoogleTwoFa();
      if (this.checked2fa !== 'G2FA') {
        this.$load(async () => {
          this.$api.profile.getProfile().then(res => {
            this.default2fa = res.data.body.verificationType;
            this.checked2fa = res.data.body.verificationType;
          });
        });
      }
    },
    resetState() {
      this.isG2Disabled = null;
    },
    sortedBy(filterName) {
      this.order = filterName === this.sortBy && this.order === DESC ? ASC : DESC;
      this.sortBy = filterName;
      this.getUserSessions();
    },
    getUserSessions() {
      this.$load(async () => {
        const response = await this.$api.profile.getUserSessions(
          this.userPublicId,
          this.sortBy,
          this.order
        );
        this.userSessionsList = response.data.body;
      });
    }
  }
};
</script>

<style lang="scss" scoped>

.holder-profile-form {
  font-weight: 500;
  font-size: 16px;
  color: var(--secondary-text-color);
}

.twofa-text {
  width: 50%;
}

.holder-profile-form p {
  color: var(--secondary-black-color);
  margin: 0;
  width: 90%;
  font-weight: 400;
  font-size: 12px;
  line-height: 19px;
}

.twofa-text p {
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  color: var(--secondary-black-color);
}

.form-group {
  position: relative;
  width: 50%;
}

.form-group input {
  padding: 15px 44px;
}

.form-group .input-password {
  position: absolute;
  top: 44px;
  right: 23px;
}

.form-group .input-text {
  position: absolute;
  top: 41.5px;
  right: 23px;
}

.holder-2fa {
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
}

.profile-btn {
  width: 100%;
  padding: 14px;
  margin-top: 30px;
}

.radio-label-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.radio-label-wrapper > span:first-child {
  font-size: 14px;
  font-weight: 600;
  line-height: 17px;
  color: var(--secondary-black-color);
}

.radio-label-wrapper:last-child {
  font-size: 12px;
  font-weight: 500;
  line-height: 19px;
  color: var(--secondary-text-color);
}

.toggleWrapper {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin: 15px 0 0;

  .toggle {
    margin-bottom: 0;
  }

  input {
    height: fit-content;
    margin: 2px 0 0;
  }

  input[type="radio"] {
    appearance: none;
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    border: 4px solid #EBEFF6;
    border-radius: 50%;
    position: relative;
    cursor: pointer;
  }

  input[type="radio"]:checked::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: var(--accent-color);
    border-radius: 50%;
  }
}

/* end */


.dialog-message-title {
  font-style: normal;
  font-weight: bold;
  font-size: 30px;
  line-height: 36px;
  color: #292B2F;
  text-align: center;
  margin: 16px 0;
  display: block;
  width: 100%;
}

.dialog-message-description {
  font-style: normal;
  font-weight: 500 !important;
  font-size: 16px !important;
  line-height: 26px;
  text-align: center;
  color: #696F79 !important;
  display: block;
  width: 100%;
  margin-bottom: 32px !important;
}

.verification-2fa-wrap {
  max-width: 612px;
  max-height: 600px;
  width: 100%;
  background: #0826551A;
  padding: 25px;
  margin: 15px 0 0 0;
}

.verification-2fa-qr-wrap {
  width: 200px;
  height: 200px;
  margin-bottom: 30px;
}

.verification-2fa-secret-title {
  display: block;
  width: 100%;
  margin-bottom: 20px;
}

.g2fa-button,
.save-btn {
  max-width: 400px;
  border-radius: 25px;
}

.save-btn {
  margin-top: 0;
}

.sessions-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sessions-header p {
  font-weight: 600;
  font-size: 14px;
  line-height: 100%;
  color: var(--secondary-black-color);
}
</style>